import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import toast from 'react-hot-toast';
import { redirectToStripeCheckout, getLTDParentsPriceId, getLTDTrailheadPriceId, getLTDCampfirePriceId } from '../lib/stripeCheckout';
import { track } from '../lib/track';

/**
 * Validates and sanitizes redirect URLs to prevent open redirects
 */
function validateRedirectUrl(url: string): string | null {
  try {
    // Parse the URL to check if it's valid
    const parsedUrl = new URL(url, window.location.origin);
    
    // Only allow same-origin redirects
    if (parsedUrl.origin !== window.location.origin) {
      console.warn('Invalid redirect URL - different origin:', url);
      return null;
    }
    
    // Allow only specific safe paths
    const allowedPaths = [
      '/',
      '/pricing/owners',
      '/login',
      '/dashboard',
      '/owner/dashboard'
    ];
    
    // Check if the path is in the allowed list
    if (!allowedPaths.includes(parsedUrl.pathname)) {
      console.warn('Invalid redirect URL - path not allowed:', parsedUrl.pathname);
      return null;
    }
    
    return parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
  } catch (error) {
    console.warn('Invalid redirect URL - parsing error:', error);
    return null;
  }
}

/**
 * AuthCallbackPage
 * 
 * Handles OAuth callbacks (Google sign-in) and email confirmations,
 * processing user types stored in sessionStorage during sign-up flow.
 */
export default function AuthCallbackPage() {
  const supabase = useSupabaseClient();
  const navigate = useNavigate();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Handle tokens from URL hash (OAuth or email confirmation)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');

        // If we have tokens in the URL, set the session (works for both OAuth and email confirmations)
        if (accessToken && refreshToken) {
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (error) throw error;

          // Clean up the URL hash after processing
          window.history.replaceState(null, '', window.location.pathname + window.location.search);

          if (data.session?.user) {
            await processUserTypesAndRedirect(data.session.user, true);
            return;
          }
        }

        // Fallback: Check for existing session (in case hash was already processed)
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) throw error;

        if (session?.user) {
          if (window.location.hash) {
            window.history.replaceState(null, '', window.location.pathname + window.location.search);
          }
          await processUserTypesAndRedirect(session.user, false);
        } else {
          // No session, redirect to login
          navigate('/login');
        }
      } catch (error) {
        console.error('Error in auth callback:', error);
        toast.error('Authentication error. Please try again.');
        navigate('/login');
      }
    };

    const processUserTypesAndRedirect = async (user: any, isNewSignup: boolean = false) => {
      // Check if there are pending user types from sign-up flow
      let pendingTypes: string | null = null;
      let parsedPendingTypes: string[] | null = null;
      try {
        pendingTypes = sessionStorage.getItem('pending_user_types');
      } catch (error) {
        console.error('Error accessing sessionStorage for pending_user_types:', error);
      }
      
      if (pendingTypes) {
        try {
          parsedPendingTypes = JSON.parse(pendingTypes);
          
          // Validate user types array
          if (!Array.isArray(parsedPendingTypes) || parsedPendingTypes.length === 0) {
            console.warn('Invalid user types format:', parsedPendingTypes);
          } else {
            // Update user types in database
            const { error: updateError } = await supabase.rpc('update_user_types', {
              p_user_id: user.id,
              p_types: parsedPendingTypes
            });

            if (updateError) {
              console.error('Error updating user types:', updateError);
              toast.error('Failed to update user profile. Please contact support.');
            }
          }

          // Clear the pending types
          try {
            sessionStorage.removeItem('pending_user_types');
          } catch (error) {
            console.error('Error clearing pending_user_types from sessionStorage:', error);
          }
        } catch (parseError) {
          console.error('Error parsing pending user types:', parseError);
          // Clear invalid data
          try {
            sessionStorage.removeItem('pending_user_types');
          } catch (error) {
            console.error('Error clearing invalid pending_user_types:', error);
          }
        }
      }

      // Check for LTD checkout intent (highest priority)
      let checkoutIntent: string | null = null;
      try {
        checkoutIntent = sessionStorage.getItem('ltd_checkout_intent');
      } catch (error) {
        console.error('Error accessing sessionStorage for ltd_checkout_intent:', error);
      }
      
      if (checkoutIntent === 'ltd-parents' && user) {
        const priceId = getLTDParentsPriceId();
        if (priceId) {
          try {
            sessionStorage.removeItem('ltd_checkout_intent');
            await redirectToStripeCheckout({
              priceId,
              planName: 'ltd-parents',
              userEmail: user.email,
            });
            return; // Don't navigate, Stripe will redirect
          } catch (error) {
            console.error('Failed to initiate checkout:', error);
            toast.error('Failed to start checkout. Please try again.');
            // Fall through to default redirect
          }
        } else {
          console.error('LTD Parents price ID not configured');
          toast.error('Payment system not configured. Please contact support.');
          try {
            sessionStorage.removeItem('ltd_checkout_intent'); // Clear invalid intent
          } catch (error) {
            console.error('Error clearing ltd_checkout_intent from sessionStorage:', error);
          }
        }
      }

      // LTD owners checkout intent (ltd-trailhead or ltd-campfire)
      if ((checkoutIntent === 'ltd-trailhead' || checkoutIntent === 'ltd-campfire') && user) {
        const priceId = checkoutIntent === 'ltd-trailhead'
          ? getLTDTrailheadPriceId()
          : getLTDCampfirePriceId();
        if (priceId) {
          try {
            sessionStorage.removeItem('ltd_checkout_intent');
            await redirectToStripeCheckout({
              priceId,
              planName: checkoutIntent,
              userEmail: user.email,
            });
            return; // Don't navigate, Stripe will redirect
          } catch (error) {
            console.error('Failed to initiate checkout:', error);
            toast.error('Failed to start checkout. Please try again.');
          }
        } else {
          toast.error('Payment system not configured. Please contact support.');
          try {
            sessionStorage.removeItem('ltd_checkout_intent');
          } catch (error) {
            console.error('Error clearing ltd_checkout_intent from sessionStorage:', error);
          }
        }
      }

      // Check for post-confirmation redirect
      let postConfirmRedirect: string | null = null;
      try {
        postConfirmRedirect = sessionStorage.getItem('post_confirm_redirect');
      } catch (error) {
        console.error('Error accessing sessionStorage for post_confirm_redirect:', error);
      }
      
      if (postConfirmRedirect) {
        try {
          sessionStorage.removeItem('post_confirm_redirect');
        } catch (error) {
          console.error('Error clearing post_confirm_redirect from sessionStorage:', error);
        }
        
        // Validate the redirect URL for security
        const validatedUrl = validateRedirectUrl(postConfirmRedirect);
        if (validatedUrl) {
          navigate(validatedUrl);
          return;
        } else {
          console.warn('Invalid post-confirmation redirect URL, falling back to default');
          // Fall through to default redirect
        }
      }

      // Check if user is a camp owner and redirect to mycamp-teaser
      try {
        // Get user types from database (source of truth)
        const { data: userTypeData } = await supabase
          .from('user_types')
          .select('type')
          .eq('user_id', user.id)
          .single();

        // Determine user types from database, metadata, or pending types
        let userTypes: string[] = [];
        if (userTypeData?.type) {
          userTypes = Array.isArray(userTypeData.type) ? userTypeData.type : [userTypeData.type];
        } else if (parsedPendingTypes) {
          userTypes = parsedPendingTypes;
        } else if (user.user_metadata?.user_types) {
          userTypes = Array.isArray(user.user_metadata.user_types) 
            ? user.user_metadata.user_types 
            : [user.user_metadata.user_types];
        }

        const isParent = userTypes.includes('parent');
        const isCampOwner = userTypes.includes('camp-owner');
        const persona = isParent && isCampOwner ? 'both' : isCampOwner ? 'camp_owner' : 'parent';
        if (isNewSignup) {
          track('signup_completed', { persona });
        }

        if (isCampOwner) {
          navigate('/mycamp-teaser');
          return;
        }
      } catch (error) {
        console.error('Error checking user types for redirect:', error);
        // Fall through to default redirect
      }

      // Default redirect to home
      navigate('/');
    };

    handleCallback();
  }, [supabase, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-gray-600">Completing sign in...</p>
      </div>
    </div>
  );
}

